import { useEffect } from 'react';
import { useEditor, useEditorState, EditorContent, type Editor } from '@tiptap/react';
import { DOC_EXTENSIONS, serializeDocMarkdown } from '../lib/documentMarkdown';

/**
 * Rich-text editor for a binder document `body`. A constrained TipTap
 * (ProseMirror) instance — bold, italic, bulleted and numbered lists only —
 * that is controlled on a normal-form Markdown string (see `documentMarkdown.ts`
 * / client-contract §3.7.1.x). `value` in, Markdown out via `onChange`; the
 * save path stores that string in `VaultItem.body` with no schema change.
 */

interface ToolbarButtonProps {
  label: string;
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}

function ToolbarButton({ label, active, onClick, children }: ToolbarButtonProps): JSX.Element {
  return (
    <button
      // Fixed internal set (Bold / Italic / lists), so the label is a stable
      // id source — not user data that could collide or change.
      id={`doc-toolbar-${label.toLowerCase().replace(/\s+/g, '-')}-button`}
      type="button"
      aria-label={label}
      aria-pressed={active}
      // Buttons must not steal focus from the editor (which would collapse the
      // selection they act on), so cancel the mousedown default.
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className={`h-7 min-w-7 px-2 rounded-hbs-sm text-[13px] font-semibold ${
        active ? 'bg-hbs-primary/10 text-hbs-primary' : 'text-hbs-text-mute hover:text-hbs-text'
      }`}
    >
      {children}
    </button>
  );
}

function Toolbar({ editor }: { editor: Editor }): JSX.Element {
  // TipTap v3's useEditor no longer re-renders on every transaction, so a
  // selection-only change (e.g. toggling Bold before typing) would leave the
  // buttons stale. Subscribe to just the active states the toolbar shows.
  const active = useEditorState({
    editor,
    selector: ({ editor: e }) => ({
      bold: e.isActive('bold'),
      italic: e.isActive('italic'),
      bulletList: e.isActive('bulletList'),
      orderedList: e.isActive('orderedList'),
    }),
  });
  return (
    <div className="flex items-center gap-1 border-b border-hbs-border px-2 py-1">
      <ToolbarButton label="Bold" active={active.bold} onClick={() => editor.chain().focus().toggleBold().run()}>
        <span className="font-bold">B</span>
      </ToolbarButton>
      <ToolbarButton label="Italic" active={active.italic} onClick={() => editor.chain().focus().toggleItalic().run()}>
        <span className="italic">I</span>
      </ToolbarButton>
      <ToolbarButton label="Bulleted list" active={active.bulletList} onClick={() => editor.chain().focus().toggleBulletList().run()}>
        •
      </ToolbarButton>
      <ToolbarButton label="Numbered list" active={active.orderedList} onClick={() => editor.chain().focus().toggleOrderedList().run()}>
        1.
      </ToolbarButton>
    </div>
  );
}

export default function DocumentBodyEditor({
  value,
  onChange,
  label = 'Body',
}: {
  value: string;
  onChange: (markdown: string) => void;
  /** Accessible name for the editing surface. Must match the visible label
   *  rendered beside it (WCAG 2.5.3), which is why the default is 'Body'. */
  label?: string;
}): JSX.Element {
  const editor = useEditor({
    extensions: DOC_EXTENSIONS,
    content: value,
    immediatelyRender: true,
    onUpdate: ({ editor }) => onChange(serializeDocMarkdown(editor)),
    // The editing surface is a contenteditable <div>, which is NOT a labelable
    // element — the page's visible <label>Body</label> cannot reach it via
    // htmlFor, so without this the editor has no accessible name at all.
    // Screen readers announce nothing, and any name-based query (including the
    // e2e harness's getByLabel('Body')) finds no target.
    editorProps: { attributes: { 'aria-label': label } },
  });

  // Reconcile external `value` changes (e.g. a form reset) without resetting
  // the cursor on every keystroke: only re-seed when the incoming Markdown
  // differs from what the editor already holds.
  useEffect(() => {
    if (!editor) return;
    if (value !== serializeDocMarkdown(editor)) {
      editor.commands.setContent(value, { emitUpdate: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  return (
    <div className="bg-hbs-surface border-[1.5px] border-hbs-border rounded-hbs-sm focus-within:border-hbs-primary focus-within:ring-4 focus-within:ring-hbs-primary/20 [&_.ProseMirror]:min-h-[8rem] [&_.ProseMirror]:px-3 [&_.ProseMirror]:py-2 [&_.ProseMirror]:text-[15px] [&_.ProseMirror]:text-hbs-text [&_.ProseMirror]:outline-none [&_.ProseMirror_strong]:font-semibold [&_.ProseMirror_em]:italic [&_.ProseMirror_ul]:list-disc [&_.ProseMirror_ul]:pl-5 [&_.ProseMirror_ol]:list-decimal [&_.ProseMirror_ol]:pl-5">
      {editor && <Toolbar editor={editor} />}
      <EditorContent editor={editor} />
    </div>
  );
}
